/* eslint-disable max-lines-per-function */
import type { interfaces } from '@patsnap/tahiti-core/shared/inversify';
import { TaskStatusServiceImpl } from './services/impls/task-status-service';
import { NoveltyCheckTaskStatusService } from './services/task-status-service';
import { InputLanguageDetectionService } from './services/input-language-detection-service';
import { InputLanguageDetectionServiceImpl } from './services/impls/input-language-detection-service';
import { NoveltyCheckTaskCreator } from './services/novelty-check-task-creator';
import { NoveltyCheckTaskCreatorImpl } from './services/impls/novelty-check-task-creator';
import { FeatureIdentify } from './services/feature-identify';
import { FeatureIdentifyImpl } from './services/impls/feature-identify';
import { ComplexRetrievalRepository, ComplexRetrievalService } from './services/complex-retrieval-service';
import { ComplexRetrievalServiceImpl } from './services/impls/complex-retrieval-service';
import { RetrievalProgressModelImpl } from './models/impls/retrieval-progress-model';
import { RetrievalProgressModel } from './models/retrieval-progress-model';
import { RetrievalProgressServiceImpl } from './services/impls/retrieval-progress-service';
import { RetrievalProgressService } from './services/retrieval-progress-service';
import { CurrentMatchedPatentModel } from './models/current-matched-patent-model';
import { CurrentMatchedPatentModelImpl } from './models/impls/current-matched-patent-model';
import { CurrentMatchedDocModel } from './models/current-matched-doc-model';
import { CurrentMatchedDocModelImpl } from './models/impls/current-matched-doc-model';
import { CurrentMatchedPaperModel } from './models/current-matched-paper-model';
import { CurrentMatchedWebsiteModel } from './models/current-matched-website-model';
import { CurrentSelectedRetrievalTypeModel } from './models/current-selected-retrieval-type-model';
import { CurrentSelectedRetrievalTypeModelImpl } from './models/impls/current-selected-retrieval-type-model';
import { ComplexRetrievalRepositoryImpl } from './repositories/impls/complex-retrieval-repository';
import { NoveltyCheckTaskStatusModelImpl } from './models/impls/novelty-check-task-status-model';
import { NoveltyCheckTaskStatusModel } from './models/novelty-check-task-status-model';
import { NoveltyCheckStepInfoModelImpl } from './models/impls/novelty-check-step-info-model';
import { NoveltyCheckStepInfoModel } from './models/novelty-check-step-info-model';
import { RetrievalResultReportModelImpl } from './models/impls/retrieval-result-report-model';
import { RetrievalResultReportModel } from './models/retrieval-result-report-model';
import { RetrievalResultReportServiceImpl } from './services/impls/retrieval-result-report-service';
import { ComparativeLiteraturePublicityService, NoveltyCheckReportTitleService, NoveltyReviewService, OriginalInputService, RetrievalElementsService, RetrievalResultReportSubtitleInfoService, TechEssentialService, TechFeaturesService } from './services/retrieval-result-report-service';
import { AdjustTechFeaturesServiceImpl } from './services/impls/adjust-tech-features-service';
import { AdjustTechFeaturesService } from './services/adjust-tech-features-service';

// 导入状态相关类
import { SearchFinishedState } from './services/states/impls/search-finished-state';
import { InitialState } from './services/states/impls/initial-state';
import { NoveltyCheckReportContextImpl } from './services/states/impls/novelty-check-report-context';
import { ResultState } from './services/states/impls/result-state';
import { NoveltyCheckReportContext } from './services/states/novelty-check-report-context';
import { SearchInputState } from './services/states/impls/search-input-state';
import { NoveltySimilarServiceImpl } from './services/impls/novelty-similar-service-impl';
import { NoveltySimilarService } from './services/novelty-similar-service';
import { NoveltySimilarRepositoryImpl } from './repositories/impls/novelty-similar-repository-impl';
import { NoveltySimilarRepository } from './repositories/novelty-similar-repository';
import { NoveltySearchTechFeatureState } from './services/states/impls/novelty-search-tech-feature-state';
import { CommandContribution } from '@patsnap/tahiti-core';
import { NoveltyCommandContribution } from './services/impls/novelty-command-contribution';
import { SummaryTechFeaturesServiceImpl } from './services/impls/summary-tech-features-service';
import { SummaryTechFeaturesService } from './services/summary-tech-features-service';
import { SummaryState } from './services/states/impls/summary-state';
import { ReportResultRefreshImpl } from './services/impls/report-result-refresh';
import { ReportResultRefresh } from './services/report-result-refresh';
import { ReportGenerationStatusService } from './services/report-generation-status-service';
import { NoveltyFeatureControlServiceImpl } from './services/impls/novelty-feature-control-service-impl';
import { NoveltyFeatureControlService } from './services/novelty-feature-control-service';
import { RetrievalElementIdentifyStartState } from './services/states/impls/retrieval-element-identify-start-state';
import { RetrievalProgressModelV2Impl } from './models/impls/retrieval-progress-model-v2';
import { RetrievalProgressModelV2 } from './models/retrieval-progress-model-v2';

// 检索要素API
import { RetrievalElementsApi } from './apis/retrieval-elements-api';
import { RetrievalElementsApiImpl } from './apis/impls/retrieval-elements-api';

// 重置任务API
import { ResetTaskApi } from './apis/reset-task-api';
import { ResetTaskApiImpl } from './apis/impls/reset-task-api';

// 重置任务服务
import { ResetTaskService } from './services/reset-task-service';
import { ResetTaskServiceImpl } from './services/impls/reset-task-service';
import { PatentConfirmState } from './services/states/impls/patent-confirm-state';
import { GenerateFeatureCompareTableState } from './services/states/impls/generate-feature-compare-table-state';
import { ManualSearchSaveApi } from './apis/manual-search-save-api';
import { ManualSearchSaveApiImpl } from './apis/impls/manual-search-save-api';
import { NoveltySearchStorageService, NoveltySearchStorageServiceImpl } from './services/novelty-search-storage-service';
import { TechnicalFeatureHighlightService } from './services/technical-feature-highlight-service';
import { TechnicalFeatureHighlightServiceImpl } from './services/impls/technical-feature-highlight-service';

// 模糊文本匹配API
import { FuzzyTextMatchApi } from './apis/fuzzy-text-match-api';
import { FuzzyTextMatchApiImpl } from './apis/impls/fuzzy-text-match-api';

// 图片转文本API
import { NoveltySearchPic2TextApi } from './apis/novelty-search-pic2text-api';
import { NoveltySearchPic2TextApiImpl } from './apis/impls/novelty-search-pic2text-api'; // 后端完成后启用
import { NoveltySearchPic2TextApiMock } from './apis/impls/mocks/novelty-search-pic2text-api-mock';

// 新颖性检索PR API
import { NoveltySearchPrApi } from './apis/novelty-search-pr-api';
import { NoveltySearchPrApiImpl } from './apis/impls/novelty-search-pr-api';

// 查新AI搜索统一API
import { NoveltyAiSearchApi } from './apis/novelty-ai-search-api';
import { NoveltyAiSearchApiImpl } from './apis/impls/novelty-ai-search-api';

// 常见证据检索服务
import { CommonEvidenceSearchService } from './services/common-evidence-search-service';
import { CommonEvidenceSearchServiceImpl } from './services/impls/common-evidence-search-service';

// 查新模块权限服务
import { NoveltyCheckPermissionService } from './services/novelty-check-permission-service';
import { NoveltyCheckPermissionServiceImpl } from './services/impls/novelty-check-permission-service-impl';

import { FTOProPermissionService } from './services/fto-pro-permission-service';
import { FTOProPermissionServiceImpl } from './services/impls/fto-pro-permission-service-impl';

export function noveltyCheckReportModule(bind: interfaces.Bind) {
    bind(NoveltyCheckTaskStatusService).to(TaskStatusServiceImpl);
    bind(InputLanguageDetectionService).to(InputLanguageDetectionServiceImpl);
    bind(NoveltyCheckTaskCreator).to(NoveltyCheckTaskCreatorImpl);
    bind(FeatureIdentify).to(FeatureIdentifyImpl);
    bind(ComplexRetrievalService).to(ComplexRetrievalServiceImpl);

    bind(RetrievalProgressModel).to(RetrievalProgressModelImpl);
    bind(RetrievalProgressService).to(RetrievalProgressServiceImpl);
    bind(RetrievalProgressModelV2).to(RetrievalProgressModelV2Impl);
    bind(CurrentMatchedPatentModel).to(CurrentMatchedPatentModelImpl);
    bind(CurrentMatchedDocModel).to(CurrentMatchedDocModelImpl);
    // 向后兼容：将旧的 PaperModel 和 WebsiteModel 绑定到新的通用实现
    bind(CurrentMatchedPaperModel).to(CurrentMatchedDocModelImpl);
    bind(CurrentMatchedWebsiteModel).to(CurrentMatchedDocModelImpl);
    bind(CurrentSelectedRetrievalTypeModel).to(CurrentSelectedRetrievalTypeModelImpl);
    bind(ComplexRetrievalRepository).to(ComplexRetrievalRepositoryImpl);
    bind(NoveltySimilarRepository).to(NoveltySimilarRepositoryImpl);
    bind(NoveltyCheckTaskStatusModel).to(NoveltyCheckTaskStatusModelImpl);
    bind(NoveltyCheckStepInfoModel).to(NoveltyCheckStepInfoModelImpl);
    bind(RetrievalResultReportModel).to(RetrievalResultReportModelImpl);
    bind(AdjustTechFeaturesService).to(AdjustTechFeaturesServiceImpl);
    bind(NoveltyFeatureControlService).to(NoveltyFeatureControlServiceImpl);
    bind(NoveltySearchStorageService).to(NoveltySearchStorageServiceImpl);
    bind(TechnicalFeatureHighlightService).to(TechnicalFeatureHighlightServiceImpl);

    bind(RetrievalResultReportServiceImpl).toSelf();
    bind(TechFeaturesService).toService(RetrievalResultReportServiceImpl);
    bind(OriginalInputService).toService(RetrievalResultReportServiceImpl);
    bind(NoveltyCheckReportTitleService).toService(RetrievalResultReportServiceImpl);
    bind(NoveltyReviewService).toService(RetrievalResultReportServiceImpl);
    bind(TechEssentialService).toService(RetrievalResultReportServiceImpl);
    bind(RetrievalResultReportSubtitleInfoService).toService(RetrievalResultReportServiceImpl);
    bind(ComparativeLiteraturePublicityService).toService(RetrievalResultReportServiceImpl);
    bind(RetrievalElementsService).toService(RetrievalResultReportServiceImpl);

    bind(NoveltySimilarService).to(NoveltySimilarServiceImpl);
    bind(SummaryTechFeaturesService).to(SummaryTechFeaturesServiceImpl);

    // 绑定状态类
    bind(InitialState).toSelf();
    bind(SummaryState).toSelf();
    bind(NoveltySearchTechFeatureState).toSelf();
    bind(SearchFinishedState).toSelf();
    bind(PatentConfirmState).toSelf();
    bind(GenerateFeatureCompareTableState).toSelf();
    bind(ResultState).toSelf().inSingletonScope();
    bind(ReportGenerationStatusService).toService(ResultState);
    bind(SearchInputState).toSelf();
    bind(RetrievalElementIdentifyStartState).toSelf();

    // 绑定上下文
    bind(NoveltyCheckReportContext).to(NoveltyCheckReportContextImpl);

    // 绑定命令
    bind(CommandContribution).to(NoveltyCommandContribution);
    bind(ReportResultRefresh).to(ReportResultRefreshImpl);

    // 绑定检索要素API
    bind(RetrievalElementsApi).to(RetrievalElementsApiImpl);

    // 绑定重置任务API
    bind(ResetTaskApi).to(ResetTaskApiImpl);

    // 绑定重置任务服务
    bind(ResetTaskService).to(ResetTaskServiceImpl);

    // 绑定手动检索保存API
    bind(ManualSearchSaveApi).to(ManualSearchSaveApiImpl);

    // 绑定模糊文本匹配API
    bind(FuzzyTextMatchApi).to(FuzzyTextMatchApiImpl);

    // 绑定图片转文本API (Mock实现，等待后端完成后切换)
    bind(NoveltySearchPic2TextApi).to(NoveltySearchPic2TextApiImpl);
    // bind(NoveltySearchPic2TextApi).to(NoveltySearchPic2TextApiMock);

    // 绑定新颖性检索PR API
    bind(NoveltySearchPrApi).to(NoveltySearchPrApiImpl);

    // 绑定查新AI搜索统一API
    bind(NoveltyAiSearchApi).to(NoveltyAiSearchApiImpl);

    // 绑定常见证据检索服务
    bind(CommonEvidenceSearchService).to(CommonEvidenceSearchServiceImpl);

    // 绑定查新模块权限服务
    bind(NoveltyCheckPermissionService).to(NoveltyCheckPermissionServiceImpl);

    // 绑定FTO模块权限服务
    bind(FTOProPermissionService).to(FTOProPermissionServiceImpl);
}
